import React, { ReactNode, useCallback, useEffect } from 'react'

import { fetchSubscription } from '@/entities/subscription'
import { SubscriptionContext, SubscriptionContextType } from '@/shared/components/Subscription/SubscriptionContext'
import { isTokenExpired } from '@/shared/lib/JWTTokenHelpers'
import { useAppDispatch, useAppSelector } from '@/shared/store'

type SubscriptionProviderProps = {
  children: ReactNode
}

// Feature mapping based on subscription tiers
const TIER_FEATURES: Record<string, string[]> = {
  'Free': ['planning'],
  'Premium': ['planning', 'runsheet'],
  'Enterprise': ['planning', 'runsheet'],
}

export const GlobalSubscriptionProvider: React.FC<SubscriptionProviderProps> = ({ children }) => {
  const dispatch = useAppDispatch()
  const session = useAppSelector(state => state.session)
  const subscriptionState = useAppSelector(state => state.subscription)
  const subscriptionData = subscriptionState.data
  const isAuthenticated = !isTokenExpired(session.accessToken) && session.token !== null

  // Fetch subscription data when authenticated
  useEffect(() => {
    if (isAuthenticated && !subscriptionData && !subscriptionState.isLoading) {
      dispatch(fetchSubscription())
    }
  }, [isAuthenticated, subscriptionData, subscriptionState.isLoading, dispatch])

  const refetch = useCallback(() => {
    if (isAuthenticated) {
      dispatch(fetchSubscription())
    }
  }, [isAuthenticated, dispatch])

  const hasFeature = useCallback((feature: string): boolean => {
    if (!isAuthenticated || !subscriptionData?.tierName) return false

    const tierFeatures = TIER_FEATURES[subscriptionData.tierName] || []

    return tierFeatures.includes(feature)
  }, [isAuthenticated, subscriptionData?.tierName])

  const hasVehicleCapacity = useCallback((requiredVehicles: number): boolean => {
    if (!isAuthenticated || !subscriptionData?.vehicleQuantity) return false

    return subscriptionData.vehicleQuantity >= requiredVehicles
  }, [isAuthenticated, subscriptionData?.vehicleQuantity])

  const isAtVehicleLimit = useCallback((): boolean => {
    if (!isAuthenticated || !subscriptionData?.currentVehicleCount || !subscriptionData?.vehicleQuantity) return false

    return subscriptionData.currentVehicleCount >= subscriptionData.vehicleQuantity
  }, [isAuthenticated, subscriptionData?.currentVehicleCount, subscriptionData?.vehicleQuantity])

  const canAccessRunsheet = useCallback((): boolean => {
    return hasFeature('runsheet')
  }, [hasFeature])

  const isFreeTier = useCallback((): boolean => {
    return subscriptionData?.tierName?.toLowerCase() === 'free'
  }, [subscriptionData?.tierName])

  const contextValue: SubscriptionContextType = {
    subscription: subscriptionData,
    isLoading: isAuthenticated ? subscriptionState.isLoading : false,
    isError: isAuthenticated ? subscriptionState.isError : false,
    isAuthenticated,
    hasFeature,
    hasVehicleCapacity,
    refetch,
    isAtVehicleLimit,
    canAccessRunsheet,
    isFreeTier,
  }

  return (
    <SubscriptionContext.Provider value={contextValue}>
      {children}
    </SubscriptionContext.Provider>
  )
}